/**
 * Consent page.
 *
 * Presented to new users (and users whose consent policy version is outdated)
 * before they access the Dashboard. Collects mandatory data-processing consent
 * and optional voice-recording consent.
 *
 * Route: `/consent`
 * Requires authentication: Yes (handled by ProtectedRoute wrapper in routes.ts)
 *
 * @module ConsentPage
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BookOpen } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { createConsent } from "@/lib/consentApi";
import { CONSENT_QUERY_KEY } from "@/hooks/useConsentStatus";
import { ROUTES } from "@/lib/routeConstants";

export default function ConsentPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [dataProcessingConsent, setDataProcessingConsent] = useState(false);
  const [voiceRecordingConsent, setVoiceRecordingConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dataProcessingConsent) return;

    setIsSubmitting(true);
    try {
      const result = await createConsent({
        data_processing_consent: dataProcessingConsent,
        voice_recording_consent: voiceRecordingConsent,
      });

      if (result) {
        // Seed the cache immediately so ConsentGate doesn't see a null snapshot
        // while the background refetch is in-flight after navigation.
        queryClient.setQueryData(CONSENT_QUERY_KEY, result);
        navigate(ROUTES.DASHBOARD, { replace: true });
      } else {
        toast.error(t("consent.page.errorMessage", { defaultValue: "We could not save your consent preferences. Please try again." }));
      }
    } catch {
      toast.error(t("consent.page.errorMessage", { defaultValue: "We could not save your consent preferences. Please try again." }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-[100dvh] items-start justify-center bg-background p-3 pb-[max(1rem,env(safe-area-inset-bottom))] sm:items-center sm:p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="p-4 text-center sm:p-6">
          <div className="flex justify-center mb-3">
            <BookOpen className="h-10 w-10 text-primary" />
          </div>
          <CardTitle className="text-2xl">
            {t("consent.page.title", { defaultValue: "Privacy & Data Consent" })}
          </CardTitle>
          <CardDescription>
            {t("consent.page.subtitle", { defaultValue: "Please review how we use your data before continuing." })}
          </CardDescription>
        </CardHeader>

        <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Mandatory: data processing */}
            <div className="space-y-3 rounded-lg border p-3 sm:p-4">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="data-processing"
                  checked={dataProcessingConsent}
                  onCheckedChange={(checked) => setDataProcessingConsent(checked === true)}
                  aria-required="true"
                />
                <div className="space-y-1">
                  <Label htmlFor="data-processing" className="font-medium cursor-pointer">
                    {t("consent.page.dataProcessingLabel", { defaultValue: "I agree to data processing" })}
                    <span className="ml-2 text-xs text-destructive font-normal">
                      {t("consent.page.dataProcessingRequired", { defaultValue: "Required" })}
                    </span>
                  </Label>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t("consent.page.dataProcessingDisclosure", {
                      defaultValue:
                        "By using DeutschGenie, you agree that simulation results, practice results, and session data may be processed by external AI APIs (such as OpenAI) for analysis and personalised feedback.",
                    })}{" "}
                    <Link
                      to={ROUTES.PRIVACY}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline-offset-2 hover:underline"
                    >
                      {t("consent.page.privacyPolicyLink", {
                        defaultValue: "Read our full Privacy Policy",
                      })}
                    </Link>
                    .
                  </p>
                </div>
              </div>
            </div>

            {/* Optional: voice recording */}
            <div className="space-y-3 rounded-lg border p-3 sm:p-4">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="voice-recording"
                  checked={voiceRecordingConsent}
                  onCheckedChange={(checked) => setVoiceRecordingConsent(checked === true)}
                />
                <div className="space-y-1">
                  <Label htmlFor="voice-recording" className="font-medium cursor-pointer">
                    {t("consent.page.voiceRecordingLabel", { defaultValue: "I agree to voice recording" })}
                  </Label>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t("consent.page.voiceRecordingDisclosure", {
                      defaultValue:
                        "Your voice may be recorded during simulation sessions. Recordings are used for simulation and practice-result analysis. Recordings are retained for 7 days and are automatically deleted after 7 days.",
                    })}
                  </p>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={!dataProcessingConsent || isSubmitting}
            >
              {isSubmitting
                ? t("consent.page.submitting", { defaultValue: "Saving…" })
                : t("consent.page.continueButton", { defaultValue: "Continue" })}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
